import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { RatingDisplay } from "../RatingDisplay";
import { SkillRatingBar } from "../SkillRatingBar";
import SkillRatingBadge from "../SkillRatingBadge";
import { SkillRatingCircle } from "../SkillRatingCircle";
import "./FeatureShowcase.css";

const FeatureShowcase: FC = () => {
  const { t, ready, i18n } = useTranslation("marketing");
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: "-100px" });

  if (!ready || !i18n.hasLoadedNamespace("marketing")) {
    return null;
  }

  return (
    <section
      ref={sectionRef}
      className="feature-showcase"
      aria-label={t("landing.showcase.ariaLabel")}
    >
      <div className="feature-showcase__bg" aria-hidden="true">
        <div className="feature-showcase__mesh feature-showcase__mesh--1" />
        <div className="feature-showcase__mesh feature-showcase__mesh--2" />
      </div>

      <div className="feature-showcase__inner">
        <motion.div
          className="feature-showcase__header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="feature-showcase__title">
            {t("landing.showcase.title")}
          </h2>
          <p className="feature-showcase__subtitle">
            {t("landing.showcase.subtitle")}
          </p>
        </motion.div>

        <div className="feature-showcase__grid">
          {/* Panel 1: Skill Rating */}
          <motion.div
            className="feature-showcase__panel"
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="feature-showcase__panel-header">
              <div className="feature-showcase__panel-icon feature-showcase__panel-icon--blue" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                </svg>
              </div>
              <h3 className="feature-showcase__panel-title">
                {t("landing.showcase.skills.title")}
              </h3>
            </div>
            <p className="feature-showcase__panel-desc">
              {t("landing.showcase.skills.description")}
            </p>

            <div className="feature-showcase__preview">
              <div className="feature-showcase__preview-item">
                <RatingDisplay
                  rating={4}
                  style="stars"
                  size="medium"
                  skillName="TypeScript"
                />
              </div>
              <div className="feature-showcase__preview-item">
                <SkillRatingBar
                  skillName="React"
                  rating={5}
                  showPercentage
                />
              </div>
              <div className="feature-showcase__preview-item">
                <RatingDisplay
                  rating={3}
                  style="dots"
                  size="medium"
                  skillName="AWS Lambda"
                />
              </div>
              <div className="feature-showcase__preview-item">
                <SkillRatingBar
                  skillName="GraphQL"
                  rating={3}
                  showPercentage
                />
              </div>
            </div>
          </motion.div>

          {/* Panel 2: Dashboard Analytics */}
          <motion.div
            className="feature-showcase__panel"
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="feature-showcase__panel-header">
              <div className="feature-showcase__panel-icon feature-showcase__panel-icon--purple" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 20V10M12 20V4M6 20v-6" />
                </svg>
              </div>
              <h3 className="feature-showcase__panel-title">
                {t("landing.showcase.analytics.title")}
              </h3>
            </div>
            <p className="feature-showcase__panel-desc">
              {t("landing.showcase.analytics.description")}
            </p>

            <div className="feature-showcase__preview">
              <div className="feature-showcase__metrics">
                <div className="feature-showcase__metric feature-showcase__metric--blue">
                  <span className="feature-showcase__metric-icon" aria-hidden="true">📊</span>
                  <span className="feature-showcase__metric-value">47</span>
                  <span className="feature-showcase__metric-label">Skills</span>
                </div>
                <div className="feature-showcase__metric feature-showcase__metric--green">
                  <span className="feature-showcase__metric-icon" aria-hidden="true">🏆</span>
                  <span className="feature-showcase__metric-value">6</span>
                  <span className="feature-showcase__metric-label">Certs</span>
                </div>
                <div className="feature-showcase__metric feature-showcase__metric--purple">
                  <span className="feature-showcase__metric-icon" aria-hidden="true">⭐</span>
                  <span className="feature-showcase__metric-value">4.2</span>
                  <span className="feature-showcase__metric-label">Avg Rating</span>
                </div>
                <div className="feature-showcase__metric feature-showcase__metric--teal">
                  <span className="feature-showcase__metric-icon" aria-hidden="true">📈</span>
                  <span className="feature-showcase__metric-value">+12</span>
                  <span className="feature-showcase__metric-label">This Month</span>
                </div>
              </div>

              <div className="feature-showcase__progress-section">
                <span className="feature-showcase__progress-title">
                  {t("landing.showcase.analytics.proficiency")}
                </span>
                <div className="feature-showcase__progress-bar">
                  <div className="feature-showcase__progress-fill" style={{ width: "72%" }} />
                </div>
                <span className="feature-showcase__progress-value">72%</span>
              </div>
            </div>
          </motion.div>

          {/* Panel 3: Certifications & Sharing */}
          <motion.div
            className="feature-showcase__panel"
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.45 }}
          >
            <div className="feature-showcase__panel-header">
              <div className="feature-showcase__panel-icon feature-showcase__panel-icon--green" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                </svg>
              </div>
              <h3 className="feature-showcase__panel-title">
                {t("landing.showcase.sharing.title")}
              </h3>
            </div>
            <p className="feature-showcase__panel-desc">
              {t("landing.showcase.sharing.description")}
            </p>

            <div className="feature-showcase__preview">
              {/* Mini profile card preview */}
              <div className="feature-showcase__profile-card">
                <div className="feature-showcase__profile-header">
                  <div className="feature-showcase__avatar" aria-hidden="true">JD</div>
                  <div className="feature-showcase__profile-info">
                    <span className="feature-showcase__profile-name">Jane Doe</span>
                    <span className="feature-showcase__profile-role">Senior Developer</span>
                  </div>
                </div>
                <div className="feature-showcase__profile-skills">
                  <div className="feature-showcase__profile-skill-row">
                    <span className="feature-showcase__skill-name">React</span>
                    <SkillRatingBadge rating={5} size="small" />
                  </div>
                  <div className="feature-showcase__profile-skill-row">
                    <span className="feature-showcase__skill-name">Node.js</span>
                    <SkillRatingBadge rating={4} size="small" />
                  </div>
                  <div className="feature-showcase__profile-skill-row">
                    <span className="feature-showcase__skill-name">AWS</span>
                    <SkillRatingCircle rating={4} size="small" />
                  </div>
                </div>
                <div className="feature-showcase__profile-footer">
                  <span className="feature-showcase__share-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                    </svg>
                    {t("landing.showcase.sharing.sharedProfile")}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FeatureShowcase;
